/**
 * PanelRenderer.tsx
 *
 * Routes by `panelType` to the correct renderer per brand guide §13 / panel-library-design §4.
 *
 * Panel types handled:
 *   kpi         → hero KPI card (text-5xl, per §4.1)
 *   bar_chart   → recharts BarChart (orientation per config, per §4.3)
 *   pivot_table → PivotTablePanel (per §4.4 / brand guide §27)
 *   table       → bounded drill-down table ≤20 rows (per §4.5)
 *   line_chart  → recharts LineChart (per §4.6)
 *
 * This component is the single dispatch point — no rendering logic lives in PanelCanvasV1.
 * Each panel type handles its own loading/error/empty states internally.
 *
 * The KPI Strip adjacency rule is a LAYOUT concern handled by PanelCanvasV1 — this
 * component only knows how to render a single KPI card (solo hero mode).
 */

import React from 'react'
import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  Legend,
} from 'recharts'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { buildChartAriaLabel, buildChartDataTable } from './chartA11y'
import { PivotTablePanel } from './PivotTablePanel'
import { PanelDomainErrorMessage } from './PanelDomainErrorMessage'
import { PanelStaleNote } from './PanelStaleNote'
import { PanelWarningCalloutGroup } from './PanelWarningCallout'
import { ComparisonOnlyDisabledPanel } from './ComparisonOnlyDisabledPanel'
import type {
  PanelType,
  PanelDataItemV1,
  KpiDisplayData,
  BarChartDisplayData,
  PivotTableDisplayData,
  TableDisplayData,
  LineChartDisplayData,
} from '@/types/DashboardV1.types'

// ─── TCS-Derived Field Info Affordance ───────────────────────────────────────

/**
 * Returns the field-path of the first `tcs.*`-namespaced entry in
 * `fieldDefinitions` that also carries a non-empty `definition` string, or
 * `undefined` when no such entry exists.
 *
 * Used by every panel type to guard AC9 (vendor-only panels render no icon):
 *   - The type contract states `fieldDefinitions` holds only `tcs.*` keys, but
 *     defensive filtering prevents a BE regression / schema-drift (a non-`tcs.*`
 *     key slipping in) from silently rendering a "TCS-DERIVED FIELD" badge over
 *     a vendor field.
 *   - Matches the logic already used by `PivotTablePanel.tsx` for the same reason.
 */
function firstTcsFieldPath(
  defs: Record<string, { definition: string; displayName: string }> | undefined,
): string | undefined {
  return Object.entries(defs ?? {}).find(
    ([k, v]) => k.startsWith('tcs.') && v.definition,
  )?.[0]
}

/**
 * Props for TcsDefinitionTooltip.
 *
 * `fieldDefinitions` is passed directly from `panelData.fieldDefinitions` and
 * may be undefined on legacy BE responses — the component null-coalesces safely.
 *
 * `fieldPath` is the dot-path key to look up (e.g. `'tcs.newOrReturning'`).
 * The component returns null when the key is absent, so vendor-field call-sites
 * produce no output without any conditional logic at the call site.
 *
 * `panelId` is required to derive a per-panel-unique tooltip content id —
 * two panels referencing the same `tcs.*` field on one dashboard MUST emit
 * distinct DOM ids so `aria-describedby` resolves deterministically per
 * WAI-ARIA 1.2 (multiple elements with the same id make the lookup pick the
 * first match in document order, which would silently de-target the second
 * panel's screen-reader announcement).
 *
 * Sizing is uniform across placements (KPI label row / CardHeader for bar,
 * table, line) — both rendered at w-5 h-5 (20px hit target around the 14px
 * icon, per brand-guide §35 anchor spec). Pivot inlines its own JSX in
 * `PivotTablePanel.tsx` and is not wired through this component.
 */
interface TcsDefinitionTooltipProps {
  fieldDefinitions: Record<string, { definition: string; displayName: string }> | undefined
  fieldPath: string
  panelId: string
}

/**
 * Renders a 14px Info icon with a tooltip that surfaces the canonical
 * definition of a TCS-derived field.
 *
 * Returns null immediately when `fieldDefinitions?.[fieldPath]` is absent
 * OR the `definition` string is empty — vendor-field call-sites and
 * any defective BE payload (empty definition) produce no icon, without
 * any conditional logic at the call site.
 *
 * Accessibility: TooltipTrigger renders as a <button>; the Radix-based
 * tooltip.tsx primitive handles Escape-to-dismiss and portal placement.
 * The per-panel-unique `id` on TooltipContent + `aria-describedby` on the
 * trigger satisfies the WCAG 2.1 AA contract documented in tooltip.tsx.
 *
 * EPIC-131 / Slice 1.2.
 */
const TcsDefinitionTooltip: React.FC<TcsDefinitionTooltipProps> = ({
  fieldDefinitions,
  fieldPath,
  panelId,
}) => {
  const entry = fieldDefinitions?.[fieldPath]
  if (!entry || !entry.definition) return null

  const tooltipId = `tcs-def-${panelId}-${fieldPath.replace(/\./g, '-')}`

  const triggerClass = cn(
    'inline-flex items-center justify-center w-5 h-5 rounded',
    'text-muted-foreground hover:text-accent focus-visible:text-accent',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'transition-colors bg-transparent border-0 cursor-pointer p-0',
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={triggerClass}
            aria-label={`About ${entry.displayName} field`}
            aria-describedby={tooltipId}
          >
            <Info size={14} aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          id={tooltipId}
          side="top"
          className="max-w-[280px] p-4"
        >
          {/* "TCS-DERIVED FIELD" badge — text-blue-300 is the brand-guide §35
              permitted exception to the no-raw-Tailwind-color rule (#1) on
              the dark popover surface. Visual uppercase via Tailwind utility,
              NOT a string transform — the underlying string stays sentence-cased
              for screen readers. */}
          <p className="text-[10px] font-semibold tracking-wider mb-2 uppercase text-blue-300">
            TCS-derived field
          </p>
          {/* Divider — brand-guide §35 specifies rgba(248, 250, 252, 0.12).
              No Tailwind utility maps to this exact alpha-on-slate value. */}
          <div
            className="h-px mb-2.5"
            style={{ background: 'rgba(248, 250, 252, 0.12)' }}
          />
          {/* Definition body — explicit normal-case defeats any inherited
              `uppercase` from table-header ancestors. Brand-guide §35. */}
          <p className="text-sm leading-relaxed text-popover-foreground font-normal normal-case">
            {entry.definition}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── YoY Δ KPI chip (brand-guide §38.4, YoY Slice 2.2) ──────────────────────

/**
 * Formats a numeric value for display inside the Δ chip subcaption.
 *
 * Plain numbers receive thousand separators (e.g. 2148 → "2,148").
 * The BE emits `breakdown` as `"<priorSeason>: <priorValue>"` where
 * `priorValue` is a raw number (no pre-formatting). This helper handles
 * the en-US locale comma-separated format only — currency shorthand is
 * not applied here because the BE response layer owns the main value
 * formatting and the FE `KpiDisplayData.value` already carries the
 * formatted string (e.g. "$340k").
 *
 * Handles edge cases: NaN input falls through to the raw string.
 */
function formatPriorValue(raw: string): string {
  const n = parseFloat(raw)
  if (!Number.isFinite(n)) return raw
  return n.toLocaleString('en-US')
}

/**
 * Parses the wire `breakdown` field into `{ priorSeason, priorValueFormatted }`.
 *
 * The BE serialises breakdown as `"<priorSeason>: <priorValue>"` —
 * e.g. `"2024: 2148"` or `"2024: 315000"`. The colon+space is the delimiter.
 * When the format doesn't match (defensive), returns `null` so callers can
 * skip the subcaption gracefully.
 */
function parsePriorBreakdown(
  breakdown: string | null | undefined,
): { priorSeason: string; priorValueFormatted: string } | null {
  if (!breakdown) return null
  const colonIdx = breakdown.indexOf(':')
  if (colonIdx === -1) return null
  const priorSeason = breakdown.slice(0, colonIdx).trim()
  const priorValueRaw = breakdown.slice(colonIdx + 1).trim()
  return {
    priorSeason,
    priorValueFormatted: formatPriorValue(priorValueRaw),
  }
}

/**
 * Arrow-up SVG icon for positive Δ chip.
 * Per mockup: 11×11, stroke="currentColor", strokeWidth="2.5".
 */
const ArrowUpIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width={11}
    height={11}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
)

/**
 * Arrow-down SVG icon for negative Δ chip.
 */
const ArrowDownIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width={11}
    height={11}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="19 12 12 19 5 12" />
  </svg>
)

/**
 * Arrow-right SVG icon for flat/zero Δ chip.
 */
const ArrowRightIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width={11}
    height={11}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
)

interface DeltaKpiChipProps {
  /** Pre-formatted delta string from the wire: "+12%", "-7%", "NEW", "0", "0%" */
  delta: string
  /** Direction signal — drives color theming. */
  direction: 'up' | 'down' | 'neutral'
}

/**
 * Δ KPI chip per brand-guide §38.4 (YoY Slice 2.2).
 *
 * Renders an inline pill with directional arrow icon + formatted delta text.
 * Colors:
 *   up      → success-emphasis text, success-subtle bg
 *   down    → danger-emphasis text, danger-subtle bg
 *   neutral → secondary text, muted bg, border
 *
 * Special case: delta === "NEW" → "NEW" text, positive green, no arrow.
 * Special case: delta === "0" → "0%" text, neutral chip (priorValue was 0).
 *
 * Chip shape matches mockup: inline-flex, pill border-radius, padding 3px 9px,
 * font-size 11.5px, font-weight 600.
 */
const DeltaKpiChip: React.FC<DeltaKpiChipProps> = ({ delta, direction }) => {
  const isNew = delta === 'NEW'
  const isZero = delta === '0'

  // Display text: "0" raw → "0%" for readability in flat chip
  const displayText = isZero ? '0%' : delta

  // Accessibility label for screen readers
  const ariaLabel =
    isNew
      ? 'New this year — no prior year data'
      : direction === 'up'
      ? `Up ${displayText} compared to prior year`
      : direction === 'down'
      ? `Down ${displayText} compared to prior year`
      : `Unchanged — ${displayText} compared to prior year`

  // Color classes per direction
  const chipClass = cn(
    'inline-flex items-center gap-[3px] rounded-full font-semibold',
    // Padding matches mockup: 3px vertical, 9px horizontal
    // font-size 11.5px falls between text-xs (.75rem=12px) and nothing smaller
    // so we inline it
    direction === 'up' || isNew
      ? 'bg-success-subtle text-success-emphasis'
      : direction === 'down'
      ? 'bg-danger-subtle text-danger-emphasis'
      : 'bg-muted text-secondary border border-border',
  )

  return (
    <div
      className={chipClass}
      role="status"
      aria-label={ariaLabel}
      style={{ padding: '3px 9px', fontSize: '11.5px', lineHeight: 1 }}
    >
      {!isNew && direction === 'up' && <ArrowUpIcon />}
      {!isNew && direction === 'down' && <ArrowDownIcon />}
      {!isNew && direction === 'neutral' && <ArrowRightIcon />}
      {displayText}
    </div>
  )
}

// ─── Color scheme for multi-season bar/line charts ───────────────────────────

/**
 * Per brand guide §4.3 + §26 multi-series color rules.
 * Index 0 = accent (primary), 1 = accent-60, 2 = secondary.
 * 4+ series triggers auto-degrade to line_chart with dasharray (handled at render time).
 */
const SERIES_COLORS = [
  'hsl(var(--accent))',
  'hsl(var(--accent) / 0.6)',
  '#94A3B8',
]

const SERIES_DASHARRAYS = [
  '0',       // solid
  '5 5',     // dashed
  '2 2',     // dotted
]

// Admin dashboard palette reference (MRRCharts) adapted for recharts bars.
const ADMIN_BAR_FILL_COLORS = [
  'rgba(255, 99, 132, 0.2)',
  'rgba(255, 159, 64, 0.2)',
  'rgba(255, 205, 86, 0.2)',
  'rgba(75, 192, 192, 0.2)',
  'rgba(54, 162, 235, 0.2)',
  'rgba(153, 102, 255, 0.2)',
  'rgba(201, 203, 207, 0.2)',
]

const ADMIN_BAR_STROKE_COLORS = [
  'rgb(255, 99, 132)',
  'rgb(255, 159, 64)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(54, 162, 235)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
]

const CHART_TICK_STYLE = {
  fontSize: 11,
  fill: 'hsl(var(--primary))',
}

function formatAxisNumber(value: string | number): string {
  if (typeof value !== 'number') return String(value)
  return value.toLocaleString('en-US')
}

function adminBarColors(index: number): { fill: string; stroke: string } {
  return {
    fill: ADMIN_BAR_FILL_COLORS[index % ADMIN_BAR_FILL_COLORS.length],
    stroke: ADMIN_BAR_STROKE_COLORS[index % ADMIN_BAR_STROKE_COLORS.length],
  }
}

interface ChartTooltipPayloadItem {
  name?: string | number
  value?: string | number | ReadonlyArray<string | number> | null
  color?: string
}

interface ChartTooltipContentProps {
  active?: boolean
  payload?: ReadonlyArray<ChartTooltipPayloadItem>
  label?: string | number
  panelTitle: string
  yAxisLabel: string
  pointStats?: Record<string, { rank: number; sharePercent: number }>
  pointCount?: number
  resolveEntryColor?: (
    entry: ChartTooltipPayloadItem,
    index: number,
    label: string | number | undefined,
  ) => string | undefined
}

function buildPointStats(
  labels: string[],
  values: number[],
): Record<string, { rank: number; sharePercent: number }> {
  const safeValues = values.map(v => (Number.isFinite(v) ? v : 0))
  const total = safeValues.reduce((sum, value) => sum + value, 0)

  const ranked = safeValues
    .map((value, index) => ({ value, index }))
    .sort((a, b) => b.value - a.value)

  const rankByIndex = new Map<number, number>()
  ranked.forEach((entry, idx) => rankByIndex.set(entry.index, idx + 1))

  const stats: Record<string, { rank: number; sharePercent: number }> = {}
  labels.forEach((label, index) => {
    const value = safeValues[index] ?? 0
    stats[label] = {
      rank: rankByIndex.get(index) ?? index + 1,
      sharePercent: total > 0 ? (value / total) * 100 : 0,
    }
  })

  return stats
}

function formatTooltipMetric(value: string | number | ReadonlyArray<string | number> | null | undefined): string {
  if (Array.isArray(value)) {
    return value.map(v => (typeof v === 'number' ? v.toLocaleString('en-US') : String(v))).join(' / ')
  }
  if (typeof value === 'number') return value.toLocaleString('en-US')
  if (value == null) return '--'
  return String(value)
}

const ChartTooltipContent: React.FC<ChartTooltipContentProps> = ({
  active,
  payload,
  label,
  panelTitle,
  yAxisLabel,
  pointStats,
  pointCount,
  resolveEntryColor,
}) => {
  if (!active || !payload || payload.length === 0) return null

  const numericValues = payload
    .map(item => (typeof item.value === 'number' ? item.value : null))
    .filter((v): v is number => v !== null)

  const total = numericValues.length > 1
    ? numericValues.reduce((sum, current) => sum + current, 0)
    : null

  const pointKey = String(label ?? '--')
  const pointStat = pointStats?.[pointKey]

  return (
    <div className="min-w-[210px] rounded-md border border-border bg-background/95 px-3 py-2.5 shadow-lg backdrop-blur-sm">
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-medium capitalize">{panelTitle}</span>
        <span className="font-semibold text-primary">{String(label ?? '--')}</span>
      </div>
      <div className="space-y-1.5">
        {payload.map((entry, idx) => (
          <div key={`tooltip-row-${idx}`} className="flex items-center justify-between gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 text-secondary">
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor:
                    resolveEntryColor?.(entry, idx, label) ??
                    entry.color ??
                    'hsl(var(--secondary))',
                }}
              />
              <span className="font-medium capitalize">{entry.name != null ? String(entry.name) : yAxisLabel}</span>
            </span>
            <span className="font-semibold text-primary">{formatTooltipMetric(entry.value)}</span>
          </div>
        ))}
      </div>

      {total !== null && (
        <div className="mt-2 border-t border-border pt-2 text-xs flex items-center justify-between">
          <span className="font-medium text-secondary">Total</span>
          <span className="font-semibold text-primary">{total.toLocaleString('en-US')}</span>
        </div>
      )}

      {pointStat && (
        <div className="mt-2 border-t border-border pt-2 text-xs space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-medium text-secondary">Rank</span>
            <span className="font-semibold text-primary">#{pointStat.rank}{pointCount ? ` of ${pointCount}` : ''}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-medium text-secondary">% of displayed total</span>
            <span className="font-semibold text-primary">{pointStat.sharePercent.toFixed(1)}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── SrOnly chart table ───────────────────────────────────────────────────────

interface ChartTableProps {
  caption: string
  xHeader: string
  yHeader: string
  rows: Array<{ x: string; y: string }>
  empty: boolean
}

const SrOnlyChartTable: React.FC<ChartTableProps> = ({ caption, xHeader, yHeader, rows, empty }) => (
  <table className="sr-only">
    <caption>{caption}</caption>
    <thead>
      <tr>
        <th scope="col">{xHeader}</th>
        <th scope="col">{yHeader}</th>
      </tr>
    </thead>
    <tbody>
      {empty ? (
        <tr><td colSpan={2}>No data available.</td></tr>
      ) : (
        rows.map((row, i) => (
          <tr key={i}>
            <td>{row.x}</td>
            <td>{row.y}</td>
          </tr>
        ))
      )}
    </tbody>
  </table>
)

// ─── Shared title class ───────────────────────────────────────────────────────

const TITLE_CLASS = 'text-xs font-semibold text-secondary uppercase tracking-widest'

// ─── renderGhostContent ───────────────────────────────────────────────────────

/**
 * Produces lightweight ghost content for a §38.7 comparison-only disabled panel.
 *
 * The ghost is a simplified static view of the panel's last-known data — rendered
 * at 35% opacity (applied by ComparisonOnlyDisabledPanel on its wrapper, not here).
 * It does NOT include charts/pivots/tables with full interactivity — just enough to
 * suggest what the panel showed with 2+ seasons (aria-hidden by the parent wrapper).
 *
 * Returns null when there is no display data (panel was never synced before the
 * filter changed) — the dashed card shell still renders, just empty under the callout.
 *
 * YoY TDD §3.5 / §38.7.
 */
function renderGhostContent(
  panelType: PanelType,
  panelData: PanelDataItemV1,
): React.ReactNode {
  if (!panelData.displayData) return null

  if (panelType === 'kpi') {
    const data = panelData.displayData as KpiDisplayData
    const deltaColor =
      data.deltaDirection === 'up'
        ? 'text-[#15803D]'
        : data.deltaDirection === 'down'
        ? 'text-destructive'
        : 'text-muted-foreground'
    return (
      <div className="py-1">
        <p className="text-4xl font-bold text-primary mb-2">
          {String(data.value ?? '--')}
        </p>
        {data.delta != null && (
          <p className={`text-xs font-medium mt-1 ${deltaColor}`}>{data.delta}</p>
        )}
        {data.breakdown != null && (
          <p className="text-xs text-muted-foreground mt-1">{data.breakdown}</p>
        )}
      </div>
    )
  }

  if (panelType === 'bar_chart' || panelType === 'line_chart') {
    // Placeholder bars — simple visual stand-in for chart data.
    // A full recharts render in a ghost is wasteful (animations, tooltip wiring).
    return (
      <div className="flex items-end gap-1 h-16 py-2" aria-hidden="true">
        {[0.7, 1.0, 0.55, 0.85, 0.45].map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-accent/40 rounded-sm"
            style={{ height: `${h * 100}%` }}
          />
        ))}
      </div>
    )
  }

  if (panelType === 'pivot_table' || panelType === 'table') {
    // Placeholder rows — shows "table-shaped" ghost without data.
    return (
      <div className="flex flex-col gap-1.5 py-1" aria-hidden="true">
        {[1, 0.8, 0.6].map((w, i) => (
          <div
            key={i}
            className="h-3 bg-muted-foreground/20 rounded"
            style={{ width: `${w * 100}%` }}
          />
        ))}
      </div>
    )
  }

  return null
}

// ─── PanelRenderer ───────────────────────────────────────────────────────────

interface PanelRendererProps {
  /** V1 panel definition — provides id, panelType, title. */
  panelType: PanelType
  title: string
  /** Panel data from GET /v1/dashboards/:id/data */
  panelData: PanelDataItemV1 | undefined
  /** Additional CSS classes on the Card root (e.g. col-span overrides). */
  className?: string
  /**
   * When true, renders a PanelStaleNote footer below panel content.
   * Used by mockup 30 (sync failed) to indicate data may be stale.
   */
  showStaleNote?: boolean
  /**
   * Human-readable date string for the stale note, e.g. "Apr 22, 2026".
   * Only used when showStaleNote is true.
   */
  staleAsOf?: string
  /**
   * Slice 3.2 — credential-aware availability.
   *
   * When true, the panel's backing domain was SKIPPED on the most recent
   * sync because the credential probed as `unavailable`. The BE preserved
   * the panel's cached row bit-identically (TDD §0.3 invariant). The
   * PanelStaleNote footer renders in the muted "(cached)" variant — the
   * ONLY visible per-panel marker per brand guide §37f.
   *
   * Composes with `showStaleNote`: when both are true, the cached variant
   * wins (the §37f banner above carries the actionable copy; the stale
   * marker would be redundant + visually noisy).
   */
  isCached?: boolean
  /**
   * Callback for the MULTI_FETCH_PARTIAL_FAILURE retry action (YoY Slice 2.3).
   *
   * When provided, the PartialFailureCallout renders a "Retry" button that
   * fires this callback. Fires the dashboard re-sync flow from the parent
   * (Canvas.tsx → PanelCanvasV1 → PanelRenderer). When absent, the Retry
   * button is omitted from the callout — the callout still renders to inform
   * the user of the partial failure.
   *
   * YoY TDD §3.4.
   */
  onRetry?: () => void
  /**
   * Called when the user clicks `[+ Add YYYY]` on the §38.7 comparison-only
   * disabled panel (YoY Slice 3.1).
   *
   * MUST route through AI chat-confirmed mutation — see
   * `feedback_no_direct_mutation_buttons_post_dashboard_save` axiom. The
   * parent creates this handler in Canvas.tsx and binds it to:
   *   `dispatch(sendEditChatMessageThunk(token, 'Compare to YYYY'))`
   *
   * When absent, the restore button renders disabled (edit session not yet
   * bootstrapped). The disabled-state panel still renders.
   *
   * YoY TDD §3.5.
   */
  onRestoreComparisonSeason?: () => void
}

export const PanelRenderer: React.FC<PanelRendererProps> = ({
  panelType,
  title,
  panelData,
  className,
  showStaleNote = false,
  staleAsOf,
  isCached = false,
  onRetry,
  onRestoreComparisonSeason,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()

  const cardClass = cn(
    'rounded-xl border border-border bg-card shadow-sm transition-shadow duration-150 ease-in-out hover:shadow-md',
    className
  )

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (!panelData) {
    const skeletonH = panelType === 'table' ? 'h-48' : 'h-32'
    return (
      <Card className={cardClass} aria-busy="true" data-testid="panel-loading-card">
        <CardHeader>
          <CardTitle className={TITLE_CLASS}>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className={`${skeletonH} w-full`} />
        </CardContent>
      </Card>
    )
  }

  // ── Sync error — envelope-driven (Slice E) ───────────────────────────────────
  //
  // Slice E: switch on syncErrorEnvelope?.code.  The panel shows a category-aware
  // chip in the centre of its content area.  The card may be dimmed depending on
  // the error category (brand guide §34.8):
  //
  //   danger (AUTH_EXPIRED, SCHEMA_DRIFT)  → opacity-60 border-destructive/25  aria-disabled
  //   warning (RATE_LIMIT, TRANSIENT_5XX, NETWORK, PLATFORM) → opacity-70 border-warning/25  aria-disabled
  //   BUSINESS_EMPTY  → no dimming, no error border  (sync succeeded, scope empty)
  //
  // requiresDomainId (legacy path, §26) still works when syncErrorEnvelope is absent.
  // When syncErrorEnvelope IS present, it takes precedence over the legacy
  // string-match heuristic.
  const envelope = panelData.syncErrorEnvelope

  if (envelope) {
    const code = envelope.code
    const isBusinessEmpty = code === 'BUSINESS_EMPTY'
    // AUTH_EXPIRED and AUTH_INVALID share the same red/danger panel treatment
    // (Slice F bugbash 2026-05-06): the chip class in PanelDomainErrorMessage
    // already groups them, so this predicate must too — otherwise AUTH_INVALID
    // panels render with a red chip but a warning/amber border (mismatch).
    const isDanger =
      code === 'AUTH_EXPIRED' ||
      code === 'AUTH_INVALID' ||
      code === 'SCHEMA_DRIFT'

    const errorCardClass = cn(
      'rounded-xl border bg-card shadow-sm',
      isBusinessEmpty
        ? 'border-border'
        : isDanger
        ? 'opacity-60 border-destructive/25'
        : 'opacity-70 border-warning/25',
      className,
    )

    const panelMinH = panelType === 'table' ? 192 : panelType === 'kpi' ? 80 : 160

    return (
      <Card className={errorCardClass} aria-disabled={!isBusinessEmpty || undefined}>
        <CardHeader>
          <CardTitle className={TITLE_CLASS}>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <PanelDomainErrorMessage
            domainName={title}
            errorEnvelope={envelope}
            panelMinHeight={panelMinH}
          />
        </CardContent>
      </Card>
    )
  }

  // ── Domain error — legacy path (requiresDomainId without envelope) ────────────
  // Panel is dimmed (opacity-60) with a light-error border, per §26.
  // No Retry button — retry lives in the top-level DomainErrorBannerStack.
  if (panelData.requiresDomainId) {
    const domainName = panelData.requiresDomainMessage ?? panelData.requiresDomainId
    const domainErrorCardClass = cn(
      'rounded-xl border bg-card shadow-sm opacity-60',
      'border-destructive/25',
    )
    return (
      <Card className={cn(domainErrorCardClass, className)} aria-disabled="true">
        <CardHeader>
          <CardTitle className={TITLE_CLASS}>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <PanelDomainErrorMessage domainName={domainName} />
        </CardContent>
      </Card>
    )
  }

  // ── Panel-level sync error (legacy string) ────────────────────────────────────
  if (panelData.status === 'error') {
    return (
      <Card className={cardClass} role="alert">
        <CardHeader>
          <CardTitle className={TITLE_CLASS}>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-destructive">{panelData.syncError ?? 'Could not load data for this panel.'}</p>
        </CardContent>
      </Card>
    )
  }

  // ── Never synced ─────────────────────────────────────────────────────────────
  if (panelData.status === 'never_synced' || !panelData.displayData) {
    return (
      <Card className={cardClass}>
        <CardHeader>
          <CardTitle className={TITLE_CLASS}>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-24 bg-muted/50 rounded text-xs text-muted-foreground">
            No data yet — click Sync to load data.
          </div>
        </CardContent>
      </Card>
    )
  }

  // ── §38.7 comparisonOnly disabled-panel state (YoY Slice 3.1) ───────────────
  //
  // When `aggregatedWarnings` contains `COMPARISON_ONLY_DISABLED_ONE_SEASON`,
  // the panel is in a visible-disabled state per mockup D (Option B — CEO-locked
  // 2026-05-27). We render `ComparisonOnlyDisabledPanel` INSTEAD of the normal
  // panel card and return early — the normal render path is skipped.
  //
  // This must run BEFORE the stale/cached note construction below because the
  // §38.7 treatment replaces the entire card shell (dashed border + overall
  // opacity + callout) — the inline stale/cached note pattern does not apply
  // to a disabled-panel surface.
  const comparisonOnlyDisabledWarning = panelData.aggregatedWarnings?.find(
    w => w.code === 'COMPARISON_ONLY_DISABLED_ONE_SEASON',
  )

  if (comparisonOnlyDisabledWarning) {
    // Build ghost content inline — each panel type provides a lightweight ghost
    // appropriate to its type. The ghost is non-interactive (pointer-events-none
    // applied by ComparisonOnlyDisabledPanel on the wrapper).
    const ghostContent = renderGhostContent(panelType, panelData)

    return (
      <ComparisonOnlyDisabledPanel
        title={title}
        warning={comparisonOnlyDisabledWarning}
        ghostContent={ghostContent}
        onRestore={onRestoreComparisonSeason}
        className={className}
      />
    )
  }

  // ── Stale / cached note element — rendered at bottom of success panels ──────
  //
  // Two modes (mutually exclusive, cached wins when both are set):
  //
  //   - cached (Slice 3.2, §37f): this panel's backing domain was skipped
  //     by the latest sync because the credential probed as `unavailable`.
  //     Render "Last synced {date} (cached)" in muted text — the ONLY
  //     visible per-panel marker. The §37f banner above carries the alert.
  //     Uses the panel's own `lastSyncedAt` as the cached-date source — the
  //     BE preserved this row, so its `lastSyncedAt` is the date of the last
  //     successful sync against this domain (could be hours, days, or years
  //     old).
  //
  //   - stale (PR 6, §31 AUTH_EXPIRED): the whole dashboard's sync failed
  //     and data may be stale. Render "Data as of {date}" in amber with a
  //     warning icon. Uses the dashboard-level `staleAsOf` prop.
  let staleNoteEl: React.ReactNode = null
  if (isCached) {
    // Format the panel's own lastSyncedAt — this is the date of the last
    // successful sync against this domain. Fall back to the dashboard-level
    // staleAsOf when the panel's own date is unavailable (shouldn't happen
    // in practice because a cached panel by definition has prior sync data,
    // but the guard keeps the suffix visible even on the cold-load edge case).
    const cachedAsOf = panelData.lastSyncedAt
      ? new Date(panelData.lastSyncedAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : staleAsOf
    if (cachedAsOf) {
      staleNoteEl = <PanelStaleNote asOf={cachedAsOf} cached />
    }
  } else if (showStaleNote && staleAsOf) {
    staleNoteEl = <PanelStaleNote asOf={staleAsOf} />
  }

  // ── Warning callout element — rendered after chart content, before stale note ─
  //
  // YoY Slice 2.3: fires only when `aggregatedWarnings` contains a recognized
  // warning code. Gated on the explicit warning — NOT on season cardinality.
  // This ensures:
  //   1. Single-season dual-mode panels (no warnings) render normally without
  //      any comparison UI cues (§38.6).
  //   2. Two-season panels where one season has zero rows (PRIOR_SEASON_NO_DATA
  //      warning present) render the informational callout (§38.5).
  //   3. Partial fan-out failure panels render the retry callout (§11.4).
  const warningCalloutEl =
    panelData.aggregatedWarnings && panelData.aggregatedWarnings.length > 0 ? (
      <PanelWarningCalloutGroup
        warnings={panelData.aggregatedWarnings}
        onRetry={onRetry}
      />
    ) : null

  // ── KPI (solo hero) ──────────────────────────────────────────────────────────
  if (panelType === 'kpi') {
    const data = panelData.displayData as KpiDisplayData

    // KPI panels have one field — show the affordance if any tcs.* key is present.
    // The FE does not know the exact field path for a KPI, so it surfaces the first
    // entry in fieldDefinitions (there is at most one per KPI panel in v1).
    const kpiFieldPath = firstTcsFieldPath(panelData.fieldDefinitions)

    // YoY Δ chip detection (brand-guide §38.4, YoY Slice 2.2).
    // The BE maps `comparison.deltaDirection` → wire `deltaDirection` (flat→neutral)
    // and `comparison.delta/priorSeason/priorValue` → `delta` + `breakdown`.
    // When `delta` and `deltaDirection` are both present, render the Δ chip +
    // subcaption pattern. When absent, fall through to the legacy breakdown path
    // (no regression on single-season panels).
    const hasYoYDelta = data.delta != null && data.deltaDirection != null

    // Parse breakdown for the subcaption only when the YoY chip fires.
    // Wire format: "2024: 2148" — rendered as "vs 2024 (2,148)".
    const priorBreakdown = hasYoYDelta
      ? parsePriorBreakdown(data.breakdown)
      : null

    return (
      <Card className={cardClass}>
        <CardContent className="p-5 pt-6">
          <div className="flex items-center gap-1 mb-3">
            <h3 className="text-xs font-semibold text-secondary uppercase tracking-widest">
              {data.label ?? title}
            </h3>
            {kpiFieldPath && (
              <TcsDefinitionTooltip
                fieldDefinitions={panelData.fieldDefinitions}
                fieldPath={kpiFieldPath}
                panelId={panelData.panelId}
              />
            )}
          </div>

          {/* Value block — centered per brand-guide §13 */}
          <div className="flex flex-col items-center">
            {/* Main value — text-5xl per brand-guide §13 */}
            <p className="text-5xl font-bold text-primary" style={{ letterSpacing: '-0.02em', lineHeight: 1.05 }}>
              {String(data.value ?? '--')}
            </p>

            {/* Label below value — matches mockup OQ 3.3 layout lock */}
            {hasYoYDelta && (
              <p
                className="text-secondary font-medium uppercase"
                style={{ fontSize: '11px', letterSpacing: '0.07em', marginTop: '4px', marginBottom: '10px' }}
              >
                {data.label ?? title}
              </p>
            )}

            {/* YoY Δ chip — rendered only when comparison data is present */}
            {hasYoYDelta && (
              <div style={{ marginBottom: '5px' }}>
                <DeltaKpiChip
                  delta={data.delta!}
                  direction={data.deltaDirection === 'up' ? 'up' : data.deltaDirection === 'down' ? 'down' : 'neutral'}
                />
              </div>
            )}

            {/* YoY subcaption — "vs 2024 (2,148)" */}
            {hasYoYDelta && priorBreakdown && (
              <span
                className="text-muted-foreground font-normal"
                style={{ fontSize: '11.5px' }}
              >
                vs {priorBreakdown.priorSeason} ({priorBreakdown.priorValueFormatted})
              </span>
            )}
          </div>

          {/* Legacy single-season breakdown — rendered only when YoY chip is absent */}
          {!hasYoYDelta && data.breakdown != null && (
            <p className="text-xs text-muted-foreground mt-1">{data.breakdown}</p>
          )}
          {warningCalloutEl}
          {staleNoteEl}
        </CardContent>
      </Card>
    )
  }

  // ── Bar chart ────────────────────────────────────────────────────────────────
  if (panelType === 'bar_chart') {
    const data = panelData.displayData as BarChartDisplayData
    const isHorizontal = data.orientation === 'horizontal'
    const hasSeries = data.series && Object.keys(data.series).length > 0
    const ariaLabel = buildChartAriaLabel('bar_chart', title)
    const tableData = buildChartDataTable('bar_chart', title, data.labels ?? [], data.values ?? [])

    // Flatten data for recharts
    const chartData = (data.labels ?? []).map((label, i) => {
      const point: Record<string, string | number> = {
        label,
        value: data.values?.[i] ?? 0,
      }
      if (hasSeries) {
        Object.entries(data.series!).forEach(([seriesName, seriesValues]) => {
          point[seriesName] = seriesValues[i] ?? 0
        })
      }
      return point
    })

    const seriesKeys = hasSeries ? Object.keys(data.series!) : ['value']
    const metricValuesForStats = hasSeries
      ? chartData.map(point =>
          seriesKeys.reduce((sum, seriesKey) => {
            const value = point[seriesKey]
            return sum + (typeof value === 'number' ? value : 0)
          }, 0)
        )
      : (data.values ?? []).map(v => (Number.isFinite(v) ? v : 0))
    const pointStats = buildPointStats((data.labels ?? []).map(String), metricValuesForStats)
    const barColorByLabel = new Map(
      (data.labels ?? []).map((label, idx) => [String(label), adminBarColors(idx).stroke]),
    )

    // Bar chart: one info icon in CardHeader for the first (and typically only) tcs.* field.
    const barFieldPath = firstTcsFieldPath(panelData.fieldDefinitions)

    return (
      <Card className={cardClass}>
        <CardHeader className="space-y-1 p-5 pb-2">
          <div className="flex items-center gap-1.5">
            <CardTitle className={cn(TITLE_CLASS, 'mb-0')}>{title}</CardTitle>
            {barFieldPath && (
              <TcsDefinitionTooltip
                fieldDefinitions={panelData.fieldDefinitions}
                fieldPath={barFieldPath}
                panelId={panelData.panelId}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          <ResponsiveContainer
            width="100%"
            height={isHorizontal ? 200 : 240}
            aria-label={ariaLabel}
          >
            <BarChart
              data={chartData}
              layout={isHorizontal ? 'vertical' : 'horizontal'}
              margin={isHorizontal ? undefined : { top: 8, right: 8, left: 0, bottom: 28 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(200, 200, 200, 0.2)" />
              {isHorizontal ? (
                <>
                  <XAxis
                    type="number"
                    tick={CHART_TICK_STYLE}
                    tickFormatter={formatAxisNumber}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    dataKey="label"
                    type="category"
                    tick={CHART_TICK_STYLE}
                    width={96}
                    axisLine={false}
                    tickLine={false}
                  />
                </>
              ) : (
                /*
                 * CTO-4 2026-05-29 — force every category label to render and
                 * rotate them 35°. Recharts' default behavior is to AUTO-skip
                 * labels when they would overlap; with 15+ bars and YoY-suffixed
                 * labels like "8th (2025)" or 20+ zips, that auto-skip drops
                 * every other label and the chart looks broken (user can't tell
                 * which bar is which). `interval={0}` forces ALL labels;
                 * rotation+textAnchor lets them fit without overlap; extra
                 * bottom margin + tightened font size keep the chart area
                 * stable.
                 */
                <>
                  <XAxis
                    dataKey="label"
                    tick={{ ...CHART_TICK_STYLE, fontSize: 10 }}
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={56}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={CHART_TICK_STYLE}
                    tickFormatter={formatAxisNumber}
                    axisLine={false}
                    tickLine={false}
                  />
                </>
              )}
              <RechartsTooltip
                cursor={false}
                content={({ active, payload, label }) => (
                  <ChartTooltipContent
                    active={active}
                    payload={payload}
                    label={label}
                    panelTitle={title}
                    yAxisLabel={isHorizontal ? 'X-axis metric' : 'Y-axis metric'}
                    pointStats={pointStats}
                    pointCount={chartData.length}
                    resolveEntryColor={
                      hasSeries
                        ? undefined
                        : (_, __, tooltipLabel) =>
                            barColorByLabel.get(String(tooltipLabel ?? ''))
                    }
                  />
                )}
              />
              {hasSeries && <Legend />}
              {seriesKeys.map((key, idx) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={hasSeries ? (SERIES_COLORS[idx] ?? SERIES_COLORS[SERIES_COLORS.length - 1]) : adminBarColors(0).fill}
                  stroke={hasSeries ? undefined : adminBarColors(0).stroke}
                  strokeWidth={hasSeries ? undefined : 1}
                  radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                  maxBarSize={36}
                  activeBar={false}
                  isAnimationActive={!prefersReducedMotion}
                >
                  {!hasSeries &&
                    chartData.map((_, barIdx) => {
                      const colors = adminBarColors(barIdx)
                      return (
                        <Cell
                          key={`${key}-bar-${barIdx}`}
                          fill={colors.fill}
                          stroke={colors.stroke}
                          strokeWidth={1}
                        />
                      )
                    })}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <SrOnlyChartTable
            caption={tableData.caption}
            xHeader={tableData.xHeader}
            yHeader={tableData.yHeader}
            rows={tableData.rows}
            empty={tableData.empty}
          />
          {warningCalloutEl}
          {staleNoteEl}
        </CardContent>
      </Card>
    )
  }

  // ── Pivot table ──────────────────────────────────────────────────────────────
  if (panelType === 'pivot_table') {
    const data = panelData.displayData as PivotTableDisplayData

    // Auto-degrade per panel library §4.4: when the matrix is too dense, the
    // BE stamps `fallbackPanelType` and we dispatch to a bar chart instead.
    // Falls back to row-totals × row-labels so the chart conveys the
    // distribution of the row dimension.
    //
    // Horizontal bars (Recharts `layout="vertical"`) — auto-degrade triggers
    // on row-density (>20 rows or ≥12 cols), so 20+ row labels would overlap
    // on an X-axis at fontSize: 11. Putting labels on the Y-axis with
    // width=96 keeps them legible per brand-guide §26.
    if (data?.fallbackPanelType === 'bar_chart') {
      const labels = data.rows.map(r => r.rowLabel)
      const values = data.rowTotals
      const ariaLabel = buildChartAriaLabel('bar_chart', title)
      const tableData = buildChartDataTable('bar_chart', title, labels, values)
      const chartData = labels.map((label, i) => ({ label, value: values[i] ?? 0 }))
      // Match the row count for vertical scrolling thresholds — 20+ rows gets a
      // taller container so individual bars stay clickable; bounded so the
      // panel never balloons indefinitely.
      const chartHeight = Math.min(Math.max(200, chartData.length * 22), 480)
      const pointStats = buildPointStats(labels.map(String), values.map(v => (Number.isFinite(v) ? v : 0)))
      const barColorByLabel = new Map(
        labels.map((label, idx) => [String(label), adminBarColors(idx).stroke]),
      )
      // Auto-degraded pivots may still carry fieldDefinitions — surface the
      // info-affordance in the CardHeader (matches the bar_chart branch).
      const fallbackFieldPath = firstTcsFieldPath(panelData.fieldDefinitions)

      return (
        <Card className={cardClass}>
          <CardHeader className="space-y-1 p-5 pb-2">
            <div className="flex items-center gap-1.5">
              <CardTitle className={cn(TITLE_CLASS, 'mb-0')}>{title}</CardTitle>
              {fallbackFieldPath && (
                <TcsDefinitionTooltip
                  fieldDefinitions={panelData.fieldDefinitions}
                  fieldPath={fallbackFieldPath}
                  panelId={panelData.panelId}
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 pt-0">
            <ResponsiveContainer width="100%" height={chartHeight} aria-label={ariaLabel}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(200, 200, 200, 0.2)" />
                <XAxis
                  type="number"
                  tick={CHART_TICK_STYLE}
                  tickFormatter={formatAxisNumber}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  dataKey="label"
                  type="category"
                  tick={CHART_TICK_STYLE}
                  width={96}
                  axisLine={false}
                  tickLine={false}
                />
                <RechartsTooltip
                  cursor={false}
                  content={({ active, payload, label }) => (
                    <ChartTooltipContent
                      active={active}
                      payload={payload}
                      label={label}
                      panelTitle={title}
                      yAxisLabel="X-axis metric"
                      pointStats={pointStats}
                      pointCount={chartData.length}
                      resolveEntryColor={(_, __, tooltipLabel) =>
                        barColorByLabel.get(String(tooltipLabel ?? ''))
                      }
                    />
                  )}
                />
                <Bar
                  dataKey="value"
                  fill={adminBarColors(0).fill}
                  stroke={adminBarColors(0).stroke}
                  strokeWidth={1}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={36}
                  activeBar={false}
                  isAnimationActive={!prefersReducedMotion}
                >
                  {chartData.map((_, barIdx) => {
                    const colors = adminBarColors(barIdx)
                    return (
                      <Cell
                        key={`pivot-fallback-bar-${barIdx}`}
                        fill={colors.fill}
                        stroke={colors.stroke}
                        strokeWidth={1}
                      />
                    )
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <SrOnlyChartTable
              caption={tableData.caption}
              xHeader={tableData.xHeader}
              yHeader={tableData.yHeader}
              rows={tableData.rows}
              empty={tableData.empty}
            />
            {warningCalloutEl}
            {staleNoteEl}
          </CardContent>
        </Card>
      )
    }

    return (
      <Card className={cardClass}>
        <CardHeader className="space-y-1 p-5 pb-2">
          <CardTitle className={cn(TITLE_CLASS, 'mb-0')}>{title}</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          <PivotTablePanel
            data={data}
            panelTitle={title}
            panelId={panelData.panelId}
            fieldDefinitions={panelData.fieldDefinitions}
          />
          {warningCalloutEl}
          {staleNoteEl}
        </CardContent>
      </Card>
    )
  }

  // ── Table ────────────────────────────────────────────────────────────────────
  if (panelType === 'table') {
    const data = panelData.displayData as TableDisplayData
    const columns = data.columns ?? []
    const rows = data.rows ?? []
    const totalCount = panelData.totalCount ?? data.totalCount ?? rows.length
    const showFooter = totalCount > rows.length

    const tableFieldPath = firstTcsFieldPath(panelData.fieldDefinitions)

    return (
      <Card className={cardClass}>
        <CardHeader className="space-y-1 p-5 pb-2">
          <div className="flex items-center gap-1.5">
            <CardTitle className={cn(TITLE_CLASS, 'mb-0')}>{title}</CardTitle>
            {tableFieldPath && (
              <TcsDefinitionTooltip
                fieldDefinitions={panelData.fieldDefinitions}
                fieldPath={tableFieldPath}
                panelId={panelData.panelId}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map(col => (
                  <TableHead
                    key={col.key}
                    className="text-xs font-medium text-secondary uppercase tracking-wide"
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(columns.length, 1)}
                    className="text-center text-xs text-muted-foreground py-4"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, rowIdx) => (
                  <TableRow key={rowIdx} className="hover:bg-muted/50">
                    {columns.map(col => (
                      <TableCell key={col.key} className="text-sm text-primary">
                        {String(row[col.key] ?? '')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {showFooter && (
            <p className="text-xs text-secondary mt-3">
              Showing {rows.length.toLocaleString('en-US')} of {totalCount.toLocaleString('en-US')} records.
            </p>
          )}
          {warningCalloutEl}
          {staleNoteEl}
        </CardContent>
      </Card>
    )
  }

  // ── Line chart ───────────────────────────────────────────────────────────────
  if (panelType === 'line_chart') {
    const data = panelData.displayData as LineChartDisplayData
    const hasSeries = data.series && Object.keys(data.series).length > 0
    const ariaLabel = buildChartAriaLabel('line_chart', title)
    const tableData = buildChartDataTable('line_chart', title, data.labels ?? [], data.values ?? [])

    const chartData = (data.labels ?? []).map((label, i) => {
      const point: Record<string, string | number> = {
        label,
        value: data.values?.[i] ?? 0,
      }
      if (hasSeries) {
        Object.entries(data.series!).forEach(([seriesName, seriesValues]) => {
          point[seriesName] = seriesValues[i] ?? 0
        })
      }
      return point
    })

    const seriesKeys = hasSeries ? Object.keys(data.series!) : ['value']
    const metricValuesForStats = hasSeries
      ? chartData.map(point =>
          seriesKeys.reduce((sum, seriesKey) => {
            const value = point[seriesKey]
            return sum + (typeof value === 'number' ? value : 0)
          }, 0)
        )
      : (data.values ?? []).map(v => (Number.isFinite(v) ? v : 0))
    const pointStats = buildPointStats((data.labels ?? []).map(String), metricValuesForStats)

    const lineFieldPath = firstTcsFieldPath(panelData.fieldDefinitions)

    return (
      <Card className={cardClass}>
        <CardHeader className="space-y-1 p-5 pb-2">
          <div className="flex items-center gap-1.5">
            <CardTitle className={cn(TITLE_CLASS, 'mb-0')}>{title}</CardTitle>
            {lineFieldPath && (
              <TcsDefinitionTooltip
                fieldDefinitions={panelData.fieldDefinitions}
                fieldPath={lineFieldPath}
                panelId={panelData.panelId}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          <ResponsiveContainer width="100%" height={200} aria-label={ariaLabel}>
            <LineChart data={chartData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(200, 200, 200, 0.2)" />
              <XAxis
                dataKey="label"
                tick={CHART_TICK_STYLE}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={CHART_TICK_STYLE}
                tickFormatter={formatAxisNumber}
                axisLine={false}
                tickLine={false}
              />
              <RechartsTooltip
                cursor={false}
                content={({ active, payload, label }) => (
                  <ChartTooltipContent
                    active={active}
                    payload={payload}
                    label={label}
                    panelTitle={title}
                    yAxisLabel="Y-axis metric"
                    pointStats={pointStats}
                    pointCount={chartData.length}
                  />
                )}
              />
              {hasSeries && <Legend />}
              {seriesKeys.map((key, idx) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={SERIES_COLORS[idx] ?? SERIES_COLORS[SERIES_COLORS.length - 1]}
                  strokeWidth={2}
                  strokeDasharray={SERIES_DASHARRAYS[idx] !== '0' ? SERIES_DASHARRAYS[idx] : undefined}
                  dot={{ r: 3, strokeWidth: 2, fill: SERIES_COLORS[idx] ?? SERIES_COLORS[0], stroke: '#fff' }}
                  isAnimationActive={!prefersReducedMotion}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <SrOnlyChartTable
            caption={tableData.caption}
            xHeader={tableData.xHeader}
            yHeader={tableData.yHeader}
            rows={tableData.rows}
            empty={tableData.empty}
          />
          {warningCalloutEl}
          {staleNoteEl}
        </CardContent>
      </Card>
    )
  }

  // ── Fallback for unknown panel type ──────────────────────────────────────────
  return (
    <Card className={cardClass}>
      <CardHeader>
        <CardTitle className={TITLE_CLASS}>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Skeleton className="h-32 w-full" data-testid="panel-fallback-skeleton" />
      </CardContent>
    </Card>
  )
}
